import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Event as EventIcon,
  School as ScholarshipIcon,
  Article as PaperIcon,
  RecordVoiceOver as SpeakerIcon,
} from '@mui/icons-material';
import eventsService, { Event, EventInput } from '../../services/events.service';
import api from '../../services/api';

const ENV = process.env.REACT_APP_ENV || 'sandbox';
const EVENTS_BASE_URL = ENV === 'production'
  ? 'https://eventscv-prod.web.app'
  : 'https://eventscv-sandbox.web.app';

const CONFERENCE_CATEGORIES = ['CONFERENCE', 'CONFERENCIA', 'B2B', 'CONFERÊNCIA'];

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default', draft: 'default',
  PUBLISHED: 'success', published: 'success',
  CANCELLED: 'error', cancelled: 'error',
  COMPLETED: 'primary', completed: 'primary',
  POSTPONED: 'warning',
  PENDING_REVIEW: 'warning',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Rascunho', draft: 'Rascunho',
  PUBLISHED: 'Publicado', published: 'Publicado',
  CANCELLED: 'Cancelado', cancelled: 'Cancelado',
  COMPLETED: 'Concluído', completed: 'Concluído',
  POSTPONED: 'Adiado',
  PENDING_REVIEW: 'Aguarda Aprovação',
};

interface ConferenceStats {
  total_inscricoes?: number;
  bolsas_pendentes?: number;
  total_papers?: number;
  total_speakers?: number;
}

const isConference = (e: Event) => {
  const cat = (e.categoria || '').toUpperCase().trim();
  return CONFERENCE_CATEGORIES.some(c => cat.includes(c));
};

const ConferenciasPage: React.FC = () => {
  const [allConferences, setAllConferences] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statsMap, setStatsMap] = useState<Record<number, ConferenceStats>>({});
  const [createDialog, setCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newConf, setNewConf] = useState<Partial<EventInput>>({
    titulo: '', data_inicio: '', local_cidade: 'Praia', categoria: 'CONFERENCE',
    tipo: 'PRESENCIAL', status: 'PUBLISHED', visibilidade: 'PUBLIC',
  });

  const fetchConferences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await eventsService.getEvents({ limit: 200 });
      const conferences = (result.data || []).filter(isConference);
      setAllConferences(conferences);

      // Load dashboard stats for each conference (non-blocking)
      conferences.forEach(async (conf) => {
        try {
          const r = await api.get(`/conference/${conf.id}/dashboard`);
          const d = r.data?.data || r.data || {};
          setStatsMap(prev => ({
            ...prev,
            [conf.id]: {
              total_inscricoes: d.registration?.total_registrations ?? 0,
              bolsas_pendentes: d.scholarships?.pending ?? 0,
              total_papers: d.papers?.total_submissions ?? 0,
              total_speakers: d.speakers?.total ?? 0,
            },
          }));
        } catch { /* stats are optional */ }
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar conferências');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConferences(); }, [fetchConferences]);

  const filtered = allConferences.filter(c => {
    const q = searchTerm.trim().toLowerCase();
    return !q || c.titulo.toLowerCase().includes(q) || (c.local_cidade || '').toLowerCase().includes(q);
  });

  const formatDate = (d?: string) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const handleCreate = async () => {
    if (!newConf.titulo || !newConf.data_inicio) {
      setCreateError('Título e data de início são obrigatórios.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await eventsService.createEvent({ ...newConf, categoria: 'CONFERENCE' } as EventInput);
      const newId = result?.data?.id;
      if (newId) {
        // Initialise conference tables and config
        await api.post('/conference/setup').catch(() => {});
        await api.put(`/conference/${newId}/config`, { enabled: 1 }).catch(() => {});
      }
      setCreateDialog(false);
      setNewConf({ titulo: '', data_inicio: '', local_cidade: 'Praia', categoria: 'CONFERENCE', tipo: 'PRESENCIAL', status: 'PUBLISHED', visibilidade: 'PUBLIC' });
      await fetchConferences();
      if (newId) {
        window.open(`${EVENTS_BASE_URL}/super-admin/conference/${newId}/settings`, '_blank');
      }
    } catch (err: any) {
      setCreateError(err?.response?.data?.error || err.message || 'Erro ao criar conferência');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Conferências</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'A carregar…' : `${filtered.length} conferência${filtered.length !== 1 ? 's' : ''} encontrada${filtered.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>
          Nova Conferência
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth size="small"
          placeholder="Pesquisar conferências…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Conferência</TableCell>
              <TableCell>Local / Data</TableCell>
              <TableCell align="center">Inscrições</TableCell>
              <TableCell align="center">Bolsas pendentes</TableCell>
              <TableCell align="center">Papers</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm ? 'Nenhuma conferência encontrada' : 'Nenhuma conferência criada'}
                  </Typography>
                  {!searchTerm && (
                    <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setCreateDialog(true)}>
                      Criar Conferência
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(conf => {
                const stats = statsMap[conf.id] || {};
                return (
                  <TableRow key={conf.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{conf.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">ID {conf.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conf.local_cidade || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(conf.data_inicio)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Total de inscrições">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <PeopleIcon fontSize="small" color="action" />
                          <Typography variant="body2">{stats.total_inscricoes ?? '—'}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {stats.bolsas_pendentes !== undefined && stats.bolsas_pendentes > 0 ? (
                        <Chip label={stats.bolsas_pendentes} size="small" color="warning" icon={<ScholarshipIcon />} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">{stats.bolsas_pendentes ?? '—'}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Papers submetidos">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <PaperIcon fontSize="small" color="action" />
                          <Typography variant="body2">{stats.total_papers ?? '—'}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[conf.status || ''] || conf.status || '—'}
                        color={statusColors[conf.status || ''] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Gerir conferência em Pagali Pass">
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          href={`${EVENTS_BASE_URL}/super-admin/conference/${conf.id}/dashboard`}
                          target="_blank"
                          rel="noopener noreferrer"
                          component="a"
                        >
                          Gerir
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => { setCreateDialog(false); setCreateError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Conferência</DialogTitle>
        <DialogContent dividers>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth label="Nome da Conferência" required
              value={newConf.titulo || ''}
              onChange={e => setNewConf(p => ({ ...p, titulo: e.target.value }))}
            />
            <TextField
              fullWidth label="Data de Início" type="datetime-local" required
              InputLabelProps={{ shrink: true }}
              value={newConf.data_inicio || ''}
              onChange={e => setNewConf(p => ({ ...p, data_inicio: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                sx={{ flex: 1 }} label="Local / Venue"
                value={newConf.local_nome || ''}
                onChange={e => setNewConf(p => ({ ...p, local_nome: e.target.value }))}
              />
              <TextField
                sx={{ flex: 1 }} label="Cidade"
                value={newConf.local_cidade || ''}
                onChange={e => setNewConf(p => ({ ...p, local_cidade: e.target.value }))}
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Modalidade</InputLabel>
              <Select
                label="Modalidade"
                value={newConf.tipo || 'PRESENCIAL'}
                onChange={e => setNewConf(p => ({ ...p, tipo: e.target.value as any }))}
              >
                <MenuItem value="PRESENCIAL">Presencial</MenuItem>
                <MenuItem value="ONLINE">Online</MenuItem>
                <MenuItem value="HIBRIDO">Híbrido</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialog(false); setCreateError(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={16} /> : 'Criar e Configurar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConferenciasPage;
